/**
 * TipoContenedorFormDialog — Crear/Editar tipo de contenedor (tubo/recipiente).
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useApiError } from "@/hooks/use-api-error";
import {
  useCreateTipoContenedor,
  useUpdateTipoContenedor,
  type TipoContenedor,
} from "@/hooks/use-muestras";

const TIPOS_MUESTRA = [
  { value: "suero", label: "Suero" },
  { value: "plasma", label: "Plasma" },
  { value: "sangre_total_edta", label: "Sangre total EDTA" },
  { value: "sangre_total_heparinizada", label: "Sangre total heparinizada" },
  { value: "orina", label: "Orina" },
  { value: "heces", label: "Heces" },
  { value: "secrecion", label: "Secreción" },
  { value: "liquido", label: "Líquido" },
  { value: "saliva", label: "Saliva" },
] as const;

const formSchema = z.object({
  nombre: z.string().min(1, "Requerido").max(100),
  color: z.string().min(1, "Requerido").max(50),
  tipo_muestra: z.string().min(1, "Requerido"),
  aditivo: z.string().default(""),
  volumen_ml: z.coerce.number().min(0.01, "Debe ser mayor a 0"),
  descripcion: z.string().default(""),
});

type FormValues = z.infer<typeof formSchema>;

interface TipoContenedorFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: TipoContenedor | null;
  onSuccess: () => void;
}

export function TipoContenedorFormDialog({
  open,
  onOpenChange,
  item,
  onSuccess,
}: TipoContenedorFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isNew = item === null;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as import("react-hook-form").Resolver<FormValues>,
    defaultValues: {
      nombre: "",
      color: "",
      tipo_muestra: "suero",
      aditivo: "",
      volumen_ml: 5,
      descripcion: "",
    },
  });

  useEffect(() => {
    if (item && !isNew) {
      form.reset({
        nombre: item.nombre,
        color: item.color,
        tipo_muestra: item.tipo_muestra,
        aditivo: item.aditivo || "",
        volumen_ml: Number(item.volumen_ml),
        descripcion: item.descripcion || "",
      });
    } else if (isNew && open) {
      form.reset();
    }
  }, [item, isNew, open, form]);

  const createMutation = useCreateTipoContenedor({
    onSuccess: () => {
      toast.success(t("configuracion.tiposContenedor.creado"));
      onSuccess();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const updateMutation = useUpdateTipoContenedor({
    onSuccess: () => {
      toast.success(t("configuracion.tiposContenedor.actualizado"));
      onSuccess();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const onSubmit = (values: FormValues) => {
    const data: import("@/hooks/use-muestras").TipoContenedorCreateData = {
      nombre: values.nombre,
      color: values.color,
      tipo_muestra: values.tipo_muestra as import("@/hooks/use-muestras").TipoMuestraBiologica,
      aditivo: values.aditivo,
      volumen_ml: String(values.volumen_ml),
      descripcion: values.descripcion,
    };
    if (isNew) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate({ id: item!.id, data });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? t("configuracion.tiposContenedor.nuevo")
              : t("configuracion.tiposContenedor.editar")}
          </DialogTitle>
          <DialogDescription>{t("configuracion.tiposContenedor.form_desc")}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="nombre"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("configuracion.tiposContenedor.fields.nombre")} *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej: Tubo rojo (sin aditivo)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("configuracion.tiposContenedor.fields.color")} *</FormLabel>
                    <FormControl>
                      <Input placeholder="Ej: rojo, lila, azul" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="volumen_ml"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("configuracion.tiposContenedor.fields.volumen")} *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min={0.01}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="tipo_muestra"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("configuracion.tiposContenedor.fields.tipo_muestra")} *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {TIPOS_MUESTRA.map((tm) => (
                        <SelectItem key={tm.value} value={tm.value}>
                          {tm.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="aditivo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("configuracion.tiposContenedor.fields.aditivo")}</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej: EDTA K2, Heparina de litio" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="descripcion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("configuracion.tiposContenedor.fields.descripcion")}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t("common.saving") : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
